import { Button, Input, Select, Textarea } from "@legal-500/legal500-design-system";
import { Controller, useForm } from "react-hook-form";
import type { AwardDictionaries, AwardState } from "../../api/awards.types.js";
import { CompanyPicker } from "../../components/CompanyPicker/CompanyPicker.js";
import { CreatableSelect } from "../../components/Form/CreatableSelect/index.js";
import { SearchableMultiSelect } from "../../components/Form/SearchableSelect/index.js";
import type { SelectOption } from "../../components/Form/SearchableSelect/index.js";
import { PageHeader } from "../../components/PageHeader/PageHeader.js";
import { formatDateTimeShort } from "../../utils/dateFormatter.js";
import {
  AWARD_SHORTLIST_TAG_OPTIONS,
  AWARD_STATUS_OPTIONS,
  YEAR_MAX,
  YEAR_MIN,
  type AwardFormValues,
} from "./awardForm.helpers.js";
import { LawyerPicker } from "./components/LawyerPicker.js";
import { PublishStatusChip } from "./components/PublishStatusChip.js";
import formStyles from "../../components/Form/forms/GenericForm.module.css";
import styles from "./AwardForm.module.css";

type AwardFormProps = {
  mode: "create" | "edit";
  initialValues: AwardFormValues;
  dictionaries: AwardDictionaries;
  /** Country nodes of the taxonomy tree, as multi-select options. */
  countryOptions: SelectOption[];
  /** The row's current publish state and last write, for the side card. Both
   * null on create: a row that does not exist yet has neither. */
  publishState: AwardState | null;
  lastSavedAt: string | null;
  submitting: boolean;
  /** The API's own message for the last failed save, shown as a banner. */
  submitError: string | null;
  /** `state` is whichever button was pressed: Save Draft or Publish. */
  onSubmit: (values: AwardFormValues, state: AwardState) => Promise<void>;
  onCancel: () => void;
  /** Edit only; opens the confirmation. */
  onDelete?: () => void;
};

const STATUS_SELECT_OPTIONS = [{ value: "", label: "Select status..." }, ...AWARD_STATUS_OPTIONS];

const SHORTLIST_TAG_SELECT_OPTIONS = [{ value: "", label: "None" }, ...AWARD_SHORTLIST_TAG_OPTIONS];

/**
 * The award form proper: fields, validation and the header actions. Loading
 * the row and the dictionaries, and turning a submission into a request, are
 * the page's job (AwardFormPage).
 *
 * react-hook-form with per-field `rules`, as PackageForm and CommentaryForm
 * use it, but without a zod schema: every value here is a picker selection or
 * a closed enum, so "present" is the whole of what there is to check.
 */
export function AwardForm({
  mode,
  initialValues,
  dictionaries,
  countryOptions,
  publishState,
  lastSavedAt,
  submitting,
  submitError,
  onSubmit,
  onCancel,
  onDelete,
}: AwardFormProps) {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AwardFormValues>({ defaultValues: initialValues });

  const company = watch("company");
  const status = watch("status");

  // Products are a closed list: staff pick one, they never add one from here.
  const productSelectOptions = [
    { value: "", label: "Select product..." },
    ...dictionaries.products.map(product => ({ value: product.id, label: product.name })),
  ];

  const submitAs = (state: AwardState) => handleSubmit(values => onSubmit(values, state))();

  return (
    // Enter inside a field must not submit: two buttons decide the state, and
    // neither is the obvious default.
    <form className={styles.wrapper} onSubmit={event => event.preventDefault()} noValidate>
      <PageHeader title={mode === "create" ? "New Award" : "Edit Award"}>
        <div className={styles.headerActions}>
          <Button type="button" variant="outlined" color="default" onClick={onCancel}>
            Cancel
          </Button>
          {onDelete && (
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onDelete}
              disabled={submitting}
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={() => submitAs("draft")}
            disabled={submitting}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={() => submitAs("published")}
            disabled={submitting}
          >
            Publish
          </Button>
        </div>
      </PageHeader>

      {submitError && (
        <div className={formStyles.formError} role="alert">
          {submitError}
        </div>
      )}

      <div className={styles.layout}>
        <section className={formStyles.formSection} aria-labelledby="award-details-heading">
          <h2 id="award-details-heading" className={styles.sectionTitle}>
            Award Details
          </h2>

          <div className={styles.fieldGrid}>
            <Controller
              name="productId"
              control={control}
              rules={{ validate: value => value !== "" || "Product is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  id="productId"
                  label="Product *"
                  options={productSelectOptions}
                  error={errors.productId?.message}
                  fullWidth
                  aria-required="true"
                />
              )}
            />

            {/* The tag shares the status's grid cell: it qualifies the status,
                and appearing in place keeps the two-column grid from reflowing
                when it comes and goes. */}
            <div className={styles.fieldStack}>
              <Controller
                name="status"
                control={control}
                rules={{ validate: value => value !== "" || "Status is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    id="status"
                    label="Status *"
                    options={STATUS_SELECT_OPTIONS}
                    error={errors.status?.message}
                    fullWidth
                    aria-required="true"
                    onChange={event => {
                      // A tag only means something on a shortlisted award, so
                      // leaving that status takes the tag with it rather than
                      // carrying a hidden value into the next save.
                      if (event.target.value !== "shortlisted") setValue("shortlistTag", "");
                      field.onChange(event);
                    }}
                  />
                )}
              />

              {status === "shortlisted" && (
                <Controller
                  name="shortlistTag"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="shortlistTag"
                      label="Shortlist tag"
                      options={SHORTLIST_TAG_SELECT_OPTIONS}
                      helperText="Internal — not shown on the website"
                      fullWidth
                    />
                  )}
                />
              )}
            </div>

            <Controller
              name="awardType"
              control={control}
              rules={{ validate: value => value !== null || "Type of Award is required" }}
              render={({ field }) => (
                <CreatableSelect
                  name="awardType"
                  label="Type of Award"
                  required
                  value={field.value}
                  options={dictionaries.awardTypes}
                  onChange={field.onChange}
                  error={errors.awardType?.message}
                />
              )}
            />

            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  name="category"
                  label="Category"
                  value={field.value}
                  options={dictionaries.categories}
                  onChange={field.onChange}
                  helperText="Optional"
                />
              )}
            />

            <Controller
              name="series"
              control={control}
              rules={{ validate: value => value !== null || "Award Series is required" }}
              render={({ field }) => (
                <CreatableSelect
                  name="series"
                  label="Award Series"
                  required
                  value={field.value}
                  options={dictionaries.series}
                  onChange={field.onChange}
                  error={errors.series?.message}
                />
              )}
            />

            <Controller
              name="year"
              control={control}
              rules={{
                validate: value =>
                  (Number.isInteger(value) && value >= YEAR_MIN && value <= YEAR_MAX) ||
                  `Year must be a whole number between ${YEAR_MIN} and ${YEAR_MAX}`,
              }}
              render={({ field }) => (
                <Input
                  id="year"
                  label="Year *"
                  type="number"
                  inputMode="numeric"
                  min={YEAR_MIN}
                  max={YEAR_MAX}
                  step={1}
                  value={Number.isNaN(field.value) ? "" : field.value}
                  // An emptied box becomes NaN rather than 0, so the rule above
                  // rejects it instead of accepting the year 0.
                  onChange={event =>
                    field.onChange(
                      event.target.value === "" ? Number.NaN : Number(event.target.value),
                    )
                  }
                  onBlur={field.onBlur}
                  error={errors.year?.message}
                  fullWidth
                  aria-required="true"
                />
              )}
            />

            <div className={styles.fullRow}>
              <Controller
                name="additionalInfo"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="additionalInfo"
                    label="Additional Info"
                    placeholder="Internal note — not shown on the website"
                    rows={3}
                    fullWidth
                  />
                )}
              />
            </div>

            <Controller
              name="company"
              control={control}
              rules={{ validate: value => value !== null || "Firm is required" }}
              render={({ field }) => (
                <CompanyPicker
                  label="Firm *"
                  value={field.value}
                  onChange={next => {
                    // The lawyer is picked from the firm's roster, so a different
                    // firm makes the current pick meaningless.
                    if (next?.id !== field.value?.id) setValue("contact", null);
                    field.onChange(next);
                  }}
                  error={errors.company?.message}
                  listboxId="award-firm-listbox"
                />
              )}
            />

            <Controller
              name="contact"
              control={control}
              rules={{ validate: value => value !== null || "Associated Lawyer is required" }}
              render={({ field }) => (
                <LawyerPicker
                  label="Associated Lawyer *"
                  value={field.value}
                  onChange={field.onChange}
                  companyId={company?.id ?? null}
                  error={errors.contact?.message}
                />
              )}
            />
          </div>
        </section>

        <aside className={formStyles.formSection} aria-labelledby="award-publishing-heading">
          <h2 id="award-publishing-heading" className={styles.sectionTitle}>
            Publishing
          </h2>

          <div className={styles.publishState}>
            <span className={formStyles.label}>Publish state</span>
            <div>
              <PublishStatusChip state={publishState ?? "draft"} />
            </div>
            {lastSavedAt && (
              <p className={styles.lastSaved}>Last saved {formatDateTimeShort(lastSavedAt)}</p>
            )}
          </div>

          <Controller
            name="jurisdictionCountryIds"
            control={control}
            render={({ field }) => (
              <SearchableMultiSelect
                name="jurisdictionCountryIds"
                label="Jurisdiction(s)"
                value={field.value}
                options={countryOptions}
                onChange={field.onChange}
                placeholder="Select countries..."
                helperText="Optional — leave empty for a multi-country series"
              />
            )}
          />
        </aside>
      </div>
    </form>
  );
}
